import { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext(null);

// LocalStorage key for the admin's own session while they are impersonating
// another account. When set, the UI shows a "Retour admin" banner and calling
// `stopImpersonation` restores this saved session in one click.
const IMPERSONATION_STASH_KEY = 'gardenbook_admin_stash';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [impersonation, setImpersonation] = useState(null); // { target_name, target_role }

  useEffect(() => {
    // Check for stored auth on mount
    const storedUser = localStorage.getItem('gardenbook_user');
    const storedToken = localStorage.getItem('gardenbook_token');
    const storedImp = localStorage.getItem(IMPERSONATION_STASH_KEY);

    if (storedUser && storedToken) {
      const u = JSON.parse(storedUser);
      setUser(u);
      setIsAuthenticated(true);
      if (storedImp) {
        try {
          const stash = JSON.parse(storedImp);
          setImpersonation({ target_name: u.name || u.email, target_role: u.role, stash });
        } catch { /* ignore */ }
      }
    }
    setIsLoading(false);
  }, []);

  const login = (userData, token) => {
    localStorage.setItem('gardenbook_user', JSON.stringify(userData));
    localStorage.setItem('gardenbook_token', token);
    setUser(userData);
    setIsAuthenticated(true);
    setImpersonation(null);
    localStorage.removeItem(IMPERSONATION_STASH_KEY);
  };

  const logout = () => {
    localStorage.removeItem('gardenbook_user');
    localStorage.removeItem('gardenbook_token');
    localStorage.removeItem(IMPERSONATION_STASH_KEY);
    setUser(null);
    setIsAuthenticated(false);
    setImpersonation(null);
  };

  const getToken = () => {
    return localStorage.getItem('gardenbook_token');
  };

  const startImpersonation = ({ token, role, company_id, target_name, session_id }) => {
    // Stash the current admin session so we can pop back
    const currentUser = JSON.parse(localStorage.getItem('gardenbook_user') || 'null');
    const currentToken = localStorage.getItem('gardenbook_token');
    const stash = { user: currentUser, token: currentToken };
    localStorage.setItem(IMPERSONATION_STASH_KEY, JSON.stringify(stash));
    if (session_id) {
      localStorage.setItem('gardenbook_imp_session', session_id);
    }

    const impUser = {
      id: company_id || (currentUser?.id ?? null),
      email: `impersonated:${target_name}`,
      name: target_name,
      role,
      company_id,
    };
    localStorage.setItem('gardenbook_user', JSON.stringify(impUser));
    localStorage.setItem('gardenbook_token', token);
    setUser(impUser);
    setImpersonation({ target_name, target_role: role, session_id, stash });
    setIsAuthenticated(true);
  };

  const stopImpersonation = () => {
    const raw = localStorage.getItem(IMPERSONATION_STASH_KEY);
    const sessionId = localStorage.getItem('gardenbook_imp_session');
    if (!raw) return null;
    try {
      const stash = JSON.parse(raw);
      if (stash?.user && stash?.token) {
        localStorage.setItem('gardenbook_user', JSON.stringify(stash.user));
        localStorage.setItem('gardenbook_token', stash.token);
        setUser(stash.user);
        setIsAuthenticated(true);
      }
    } catch { /* ignore */ }
    localStorage.removeItem(IMPERSONATION_STASH_KEY);
    localStorage.removeItem('gardenbook_imp_session');
    setImpersonation(null);
    return sessionId; // caller can use it to POST /admin/impersonation/end
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse-soft">
          <div className="w-16 h-16 bg-primary/20 rounded-full" />
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{
      user, isAuthenticated, login, logout, getToken,
      impersonation, startImpersonation, stopImpersonation,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
